import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, BuildingIcon, PackageIcon, UserIcon, MonitorIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, GlobeIcon, FileTextIcon, ScaleIcon, PenLineIcon, CreditCardIcon, SmartphoneIcon, StoreIcon, ArrowRightIcon, ClockIcon, RefreshCwIcon, SparklesIcon, Building2Icon, TagIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RESOURCES, USAGE_TYPES, CONTRACT_TEMPLATES } from '../../data/windsor';
import type { ResourceKind, UsageType } from '../../types/loisirs';
const KIND_ICON: Record<ResourceKind, typeof BuildingIcon> = {
  plateau: BuildingIcon,
  equipement: PackageIcon,
  humain: UserIcon,
  enligne: MonitorIcon
};
const KIND_LABEL: Record<ResourceKind, {
  fr: string;
  en: string;
}> = {
  plateau: {
    fr: 'Plateau',
    en: 'Facility'
  },
  equipement: {
    fr: 'Équipement',
    en: 'Equipment'
  },
  humain: {
    fr: 'Ressource humaine',
    en: 'Human resource'
  },
  enligne: {
    fr: 'En ligne',
    en: 'Online'
  }
};

// --- RESOURCES --------------------------------------------------------------

export function ResourcesPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [expanded, setExpanded] = useState<string[]>(['res-lemay', 'res-rl']);
  /** Editable booking rules, keyed by resource id. */
  const [resources, setResources] = useState(RESOURCES);
  /** Resource whose rules panel is open. */
  const [editing, setEditing] = useState<string | null>(null);
  const patch = (id: string, changes: Partial<(typeof RESOURCES)[number]>) => setResources(prev => prev.map(r => r.id === id ? {
    ...r,
    ...changes
  } : r));

  /** Undefined means "no restriction", which we display as both audiences. */
  const audienceOf = (resource: (typeof RESOURCES)[number]) => resource.bookableBy && resource.bookableBy.length > 0 ? resource.bookableBy : ['organisation', 'citoyen'] as const;
  const toggleAudience = (resource: (typeof RESOURCES)[number], who: 'organisation' | 'citoyen') => {
    const current = audienceOf(resource);
    const next = current.includes(who) ? current.filter(w => w !== who) : [...current, who];
    // Never leave a bookable resource with no possible client.
    if (next.length === 0) return;
    patch(resource.id, {
      bookableBy: next as ('organisation' | 'citoyen')[]
    });
  };
  const toggleUsage = (resource: (typeof RESOURCES)[number], usageId: string) => {
    const current = resource.allowedUsageTypeIds ?? USAGE_TYPES.map(u => u.id);
    const next = current.includes(usageId) ? current.filter(id => id !== usageId) : [...current, usageId];
    patch(resource.id, {
      allowedUsageTypeIds: next
    });
  };
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const parents = resources.filter(r => !r.parentId);
  const childrenOf = (id: string) => resources.filter(r => r.parentId === id);
  const Row = ({
    resource,
    depth
  }: {
    resource: (typeof RESOURCES)[number];
    depth: number;
  }) => {
    const kids = childrenOf(resource.id);
    const open = expanded.includes(resource.id);
    const Icon = KIND_ICON[resource.kind];
    return <>
        <li className="group flex items-center gap-2 rounded-lg px-2 py-2.5 hover:bg-slate-50 transition-colors" style={{
        paddingLeft: 8 + depth * 22
      }} data-id="element-2958" data-mp="1iuidx0c9lq9h">
          {kids.length > 0 ? <button onClick={() => setExpanded(prev => open ? prev.filter(id => id !== resource.id) : [...prev, resource.id])} aria-label={open ? t('Replier', 'Collapse') : t('Déplier', 'Expand')} aria-expanded={open} className="p-0.5 rounded text-slate-400 hover:text-slate-700 shrink-0" data-id="element-2959" data-mp="1rnbwp7qx77s3">
              {open ? <ChevronDownIcon className="w-4 h-4" data-id="element-2960" data-mp="1cu1a7l9qy1zk" /> : <ChevronRightIcon className="w-4 h-4" data-id="element-2961" data-mp="1hrjo7q2mf7ef" />}
            </button> : <span className="w-5 shrink-0" data-id="element-2962" data-mp="1mk326o5804jr" />}

          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${resource.kind === 'plateau' ? 'bg-indigo-50 text-[#4F46E5]' : resource.kind === 'equipement' ? 'bg-amber-50 text-amber-600' : resource.kind === 'humain' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-2963" data-mp="1rie8wq07xyxr">
            <Icon className="w-3.5 h-3.5" data-id="element-2964" data-mp="675w9tyzuxgn" />
          </span>

          <div className="min-w-0 flex-1" data-id="element-2965" data-mp="b57bg3taucmu">
            <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2966" data-mp="b5z8lj2joif8">
              {resource.name[lang]}
              {!resource.bookable && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold uppercase" data-id="element-2967" data-mp="3uy9jnxfv51nr">
                  {t('regroupement', 'grouping')}
                </span>}
            </p>
            <p className="text-xs text-slate-400 truncate" data-id="element-2968" data-mp="3l2y45cwmrqey">
              {KIND_LABEL[resource.kind][lang]}
              {resource.address && ` · ${resource.address}`}
              {resource.capacity ? ` · ${resource.capacity} pl.` : ''}
              {resource.bookable && resource.rateCitizen !== undefined ? ` · ${resource.rateCitizen === 0 ? t('gratuit', 'free') : `${money(resource.rateCitizen)}/h`}` : ''}
            </p>

            {/* Booking rules, summarized */}
            {resource.bookable && <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-id="element-2969" data-mp="1kd0jwbktesxt">
                {audienceOf(resource).map(who => <span key={who} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-[#4F46E5] text-[10px] font-semibold border border-indigo-100" data-id="element-2970" data-mp="1oevlziaklacn">
                    {who === 'organisation' ? <Building2Icon className="w-3 h-3" data-id="element-2971" data-mp="10ohpnfkycpc9" /> : <UserIcon className="w-3 h-3" data-id="element-2972" data-mp="15fhncn1wbbft" />}
                    {who === 'organisation' ? t('Organismes', 'Organizations') : t('Citoyens', 'Citizens')}
                  </span>)}
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold" data-id="element-2973" data-mp="wlbnaj0ynib2">
                  <TagIcon className="w-3 h-3" data-id="element-2974" data-mp="mprrrxqb6lmu" />
                  {resource.allowedUsageTypeIds ? t(`${resource.allowedUsageTypeIds.length} types d’activité`, `${resource.allowedUsageTypeIds.length} activity types`) : t('Tous les types', 'All types')}
                </span>
              </div>}
          </div>

          <div className="flex items-center gap-1 shrink-0" data-id="element-2975" data-mp="80yiwyfe2h8s">
            {resource.bookable && <button onClick={() => setEditing(editing === resource.id ? null : resource.id)} aria-expanded={editing === resource.id} aria-label={t('Règles de réservation', 'Booking rules')} title={t('Règles de réservation', 'Booking rules')} className={`p-2 rounded-lg transition-colors ${editing === resource.id ? 'text-[#4F46E5] bg-indigo-50' : 'text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50'}`} data-id="element-2976" data-mp="m0rhth12k3ru">
                <SlidersHorizontalIcon className="w-4 h-4" data-id="element-2977" data-mp="3mik6qkakdc7g" />
              </button>}
            <button aria-label={t('Modifier la ressource', 'Edit resource')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-id="element-2978" data-mp="3ri6o95xfdem2">
              <PencilIcon className="w-4 h-4" data-id="element-2979" data-mp="33scbsph0ps2c" />
            </button>
            <button aria-label={t('Retirer la ressource', 'Remove resource')} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-id="element-2980" data-mp="33qj0gwvgmjo0">
              <TrashIcon className="w-4 h-4" data-id="element-2981" data-mp="2g16us6zcctqg" />
            </button>
          </div>
        </li>

        {/* Booking rules editor */}
        {editing === resource.id && <li className="bg-slate-50/70 px-4 py-4" style={{
        paddingLeft: 8 + depth * 22 + 44
      }} data-id="element-2982" data-mp="125ylcokb99jx">
            <div className="space-y-4" data-id="element-2983" data-mp="y3tqxnm1g8w8">
              <div data-id="element-2984" data-mp="t5sa79c34lnx">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2985" data-mp="t5a2ngjz2l0p">
                  {t('Réservable par', 'Bookable by')}
                </p>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-2986" data-mp="j8xe0x3aaxct">
                  {[{
                who: 'organisation' as const,
                label: t('Organismes', 'Organizations'),
                icon: Building2Icon
              }, {
                who: 'citoyen' as const,
                label: t('Citoyens', 'Citizens'),
                icon: UserIcon
              }].map(option => {
                const on = audienceOf(resource).includes(option.who);
                const OptionIcon = option.icon;
                return <button key={option.who} onClick={() => toggleAudience(resource, option.who)} aria-pressed={on} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${on ? 'border-[#4F46E5] bg-indigo-50/60 text-[#3730A3] ring-1 ring-[#4F46E5]/20' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`} data-id="element-2987" data-mp="2ps3qul1u0fbl">
                        <OptionIcon className="w-4 h-4" data-id="element-2988" data-mp="2ft4gmy1f4bmh" />
                        {option.label}
                        {on && <CheckIcon className="w-3.5 h-3.5" data-id="element-2989" data-mp="1gn7clm518f" />}
                      </button>;
              })}
                </div>
                <p className="text-xs text-slate-500 mt-2" data-id="element-2990" data-mp="3kpf1cbq48m7">
                  {audienceOf(resource).length === 2 ? t('Les organismes reconnus et les citoyens peuvent réserver cette ressource.', 'Recognized organizations and citizens can both book this resource.') : audienceOf(resource)[0] === 'organisation' ? t('Seuls les organismes reconnus peuvent réserver. Les demandes citoyennes seront bloquées.', 'Only recognized organizations can book. Citizen requests will be blocked.') : t('Seuls les citoyens peuvent réserver cette ressource.', 'Only citizens can book this resource.')}
                </p>
              </div>

              <div data-id="element-2991" data-mp="1a2vw6m69b8j8">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap" data-id="element-2992" data-mp="1a347i9mqe5sb">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2993" data-mp="1jv730td8dson">
                    {t('Types d’activité autorisés', 'Permitted activity types')}
                  </p>
                  <button onClick={() => patch(resource.id, {
                allowedUsageTypeIds: resource.allowedUsageTypeIds ? undefined : USAGE_TYPES.map(u => u.id)
              })} className="text-xs font-semibold text-[#4F46E5] hover:underline" data-id="element-2994" data-mp="15wgwkn4iugnm">
                    {resource.allowedUsageTypeIds ? t('Autoriser tous les types', 'Allow every type') : t('Restreindre les types', 'Restrict types')}
                  </button>
                </div>

                {resource.allowedUsageTypeIds ? <div className="flex items-center gap-2 flex-wrap" data-id="element-2995" data-mp="2ovom146mi1ne">
                    {USAGE_TYPES.map(usage => {
                const on = resource.allowedUsageTypeIds!.includes(usage.id);
                return <button key={usage.id} onClick={() => toggleUsage(resource, usage.id)} aria-pressed={on} className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm transition-colors ${on ? 'border-slate-300 bg-white text-slate-800 font-medium' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'}`} data-id="element-2996" data-mp="32uo9w9mbacii">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{
                    backgroundColor: on ? usage.color : '#CBD5E1'
                  }} data-id="element-2997" data-mp="1rfr6tvtalms5" />
                          {usage.label[lang]}
                        </button>;
              })}
                  </div> : <p className="text-sm text-slate-500" data-id="element-2998" data-mp="1xu99qg8gag2">
                    {t('Aucune restriction : cette ressource accepte tous les types d’activité.', 'No restriction: this resource accepts every activity type.')}
                  </p>}
              </div>
            </div>
          </li>}

        {open && kids.map(kid => <Row key={kid.id} resource={kid} depth={depth + 1} data-id="element-2999" data-mp="2rg32h2ng6vem" />)}
      </>;
  };
  const groups: {
    kind: ResourceKind;
    title: string;
  }[] = [{
    kind: 'plateau',
    title: t('Plateaux et lieux', 'Facilities and venues')
  }, {
    kind: 'equipement',
    title: t('Équipements prêtables', 'Loanable equipment')
  }, {
    kind: 'humain',
    title: t('Ressources humaines', 'Human resources')
  }, {
    kind: 'enligne',
    title: t('En ligne', 'Online')
  }];
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3000" data-mp="23jgenagyydmg">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3001" data-mp="28ijcoayj7jkx">
        <div data-id="element-3002" data-mp="1tmbsvbr1ck66">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3003" data-mp="1tljz86h00wij">
            {t('Ressources', 'Resources')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3004" data-mp="1kmeycthysr37">
            {t('Tout ce qui peut être réservé : plateaux, salles, équipements, personnel et créneaux en ligne. Les salles se rattachent au bâtiment qui les contient.', 'Everything that can be booked: facilities, rooms, equipment, staff and online slots. Rooms attach to the building that contains them.')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap" data-id="element-3005" data-mp="3hlxdwbdjxmn3">
          {[{
          kind: 'plateau' as const,
          label: t('Ajouter un plateau', 'Add a facility')
        }, {
          kind: 'equipement' as const,
          label: t('Ajouter un équipement', 'Add equipment')
        }, {
          kind: 'humain' as const,
          label: t('Ajouter une ressource humaine', 'Add a human resource')
        }].map((action, i) => <button key={action.kind} className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg transition-colors ${i === 0 ? 'bg-[#4F46E5] hover:bg-[#4338CA] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3006" data-mp="55aan6hil2eq" data-mpi={i}>
              <PlusIcon className="w-4 h-4" data-id="element-3007" data-mp="nv9xrntso7fi" />
              {action.label}
            </button>)}
        </div>
      </div>

      <div className="space-y-5" data-id="element-3008" data-mp="qemz96qhfujq">
        {groups.map(group => {
        const items = parents.filter(r => r.kind === group.kind);
        if (items.length === 0) return null;
        return <div key={group.kind} data-id="element-3009" data-mp="10cuhdswicinb">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-3010" data-mp="10brppird2c6c">
                {group.title}
                <span className="ml-2 text-slate-300" data-id="element-3011" data-mp="159ks00pjmm1c">{items.length}</span>
              </h3>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-id="element-3012" data-mp="19czbx2glrmk2">
                {items.map(resource => <Row key={resource.id} resource={resource} depth={0} data-id="element-3013" data-mp="1j3c6lni4sw3x" />)}
              </ul>
            </div>;
      })}
      </div>
    </section>;
}

// --- USAGE TYPES ------------------------------------------------------------

const PALETTE = ['#4F46E5', '#0284C7', '#DB2777', '#7C3AED', '#0D9488', '#B45309', '#CA8A04', '#059669', '#9333EA', '#2563EB', '#475569', '#64748B'];
export function UsageTypesPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [types, setTypes] = useState<UsageType[]>(USAGE_TYPES);
  const [picking, setPicking] = useState<string | null>(null);
  const update = (id: string, patch: Partial<UsageType>) => setTypes(prev => prev.map(u => u.id === id ? {
    ...u,
    ...patch
  } : u));
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3014" data-mp="1znag1eyelc09">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3015" data-mp="1znk9xdmkk3b8">
        <div data-id="element-3016" data-mp="1upirmuwyw80t">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3017" data-mp="1uq0s63qnx8ej">
            {t('Types d’utilisation', 'Usage types')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3018" data-mp="24mnc8qb5zvfi">
            {t('Le type d’utilisation pilote la couleur au calendrier, le contrat proposé par défaut, la tarification et la visibilité publique de la plage.', 'The usage type drives the calendar colour, the default contract, the pricing and whether the slot is publicly visible.')}
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-3019" data-mp="3sc1m0v0qwerq">
          <PlusIcon className="w-4 h-4" data-id="element-3020" data-mp="3ilp65g3vl9rx" />
          {t('Nouveau type', 'New type')}
        </button>
      </div>

      <ul className="space-y-2" data-id="element-3021" data-mp="a5ncr9df8szo">
        {types.map(usage => <li key={usage.id} className="rounded-lg border border-slate-200 px-4 py-3.5" data-id="element-3022" data-mp="a4c877c5jh7i">
            <div className="flex items-start gap-3 flex-wrap" data-id="element-3023" data-mp="15qpnc6ok26q">
              {/* Colour */}
              <div className="relative shrink-0" data-id="element-3024" data-mp="64bhoobs3fk3">
                <button onClick={() => setPicking(picking === usage.id ? null : usage.id)} aria-label={t('Changer la couleur', 'Change colour')} className="w-8 h-8 rounded-lg border-2 border-white ring-1 ring-slate-200" style={{
              backgroundColor: usage.color
            }} data-id="element-3025" data-mp="630fuu1w8hvm" />
                {picking === usage.id && <div className="absolute z-10 mt-1 grid grid-cols-6 gap-1 p-2 bg-white rounded-lg border border-slate-200 shadow-lg" data-id="element-3026" data-mp="2r60nizlxp7dz">
                    {PALETTE.map(color => <button key={color} onClick={() => {
                update(usage.id, {
                  color
                });
                setPicking(null);
              }} aria-label={color} className="w-6 h-6 rounded-md ring-1 ring-slate-200 flex items-center justify-center" style={{
                backgroundColor: color
              }} data-id="element-3027" data-mp="2w76g8xflguni">
                        {usage.color === color && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-3028" data-mp="23g6bgk9fvmus" />}
                      </button>)}
                  </div>}
              </div>

              <div className="min-w-0 flex-1" data-id="element-3029" data-mp="1ftkim3hurmn0">
                <p className="text-sm font-medium text-slate-800" data-id="element-3030" data-mp="1fuvme3basue4">
                  {usage.label[lang]}
                </p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-id="element-3031" data-mp="1tqfwqacyo49x">
                  <label className="sr-only" htmlFor={`tpl-${usage.id}`} data-id="element-3032" data-mp="1osed9obmi2xx">
                    {t('Contrat par défaut', 'Default contract')}
                  </label>
                  <select id={`tpl-${usage.id}`} value={usage.defaultContractTemplateId ?? ''} onChange={e => update(usage.id, {
                defaultContractTemplateId: e.target.value || undefined
              })} className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5]" data-id="element-3033" data-mp="1yqu4dranhc7f">
                    <option value="" data-id="element-3034" data-mp="1f69v10qyn9jd">
                      {t('Aucun contrat', 'No contract')}
                    </option>
                    {CONTRACT_TEMPLATES.map(tpl => <option key={tpl.id} value={tpl.id} data-id="element-3035" data-mp="1o5erm1iyossw">
                        {tpl.name[lang]}
                      </option>)}
                  </select>

                  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3036" data-mp="1xvq7mmfx6sg5">
                    {[{
                  id: 'gratuit' as const,
                  label: t('Gratuit', 'Free')
                }, {
                  id: 'tarif' as const,
                  label: t('Tarifé', 'Charged')
                }].map(option => <button key={option.id} onClick={() => update(usage.id, {
                  defaultPricing: option.id
                })} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${usage.defaultPricing === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3037" data-mp="2bsvbcfs5vf88">
                        {option.label}
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0" data-id="element-3038" data-mp="su75rkz9ssp4">
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-id="element-3039" data-mp="nxzzfr2ypf6n">
                  <GlobeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3040" data-mp="ix2gvde1ij4m" />
                  {usage.showOnPublicCalendar ? t('public', 'public') : t('interne', 'internal')}
                </span>
                <button type="button" role="switch" aria-checked={usage.showOnPublicCalendar} aria-label={t('Visible sur les calendriers publics', 'Visible on public calendars')} onClick={() => update(usage.id, {
              showOnPublicCalendar: !usage.showOnPublicCalendar
            })} className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${usage.showOnPublicCalendar ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3041" data-mp="33mi5q2tp7xrm">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${usage.showOnPublicCalendar ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3042" data-mp="155g2ay61a9n0" />
                </button>
              </div>
            </div>
          </li>)}
      </ul>
    </section>;
}

// --- CONTRACT TEMPLATES -----------------------------------------------------

export function ContractsPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [templates, setTemplates] = useState(CONTRACT_TEMPLATES);
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3043" data-mp="2zlesog6bmznr">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3044" data-mp="2umv56bc2xfta">
        <div data-id="element-3045" data-mp="2ukqr50kgv0fa">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3046" data-mp="2ul0l0z8mtrq9">
            {t('Modèles de contrats', 'Contract templates')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3047" data-mp="34jzq67u6v58i">
            {t('Un modèle par type de location. Le greffe rédige certaines ententes hors plateforme : elles sont alors importées au dossier plutôt que générées.', 'One template per rental type. The clerk’s office drafts some agreements off-platform: those are imported into the file rather than generated.')}
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-3048" data-mp="35jq88cblcum8">
          <PlusIcon className="w-4 h-4" data-id="element-3049" data-mp="3fbe36n7cwtsg" />
          {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      <ul className="space-y-2" data-id="element-3050" data-mp="2inpxj4ohetps">
        {templates.map(tpl => <li key={tpl.id} className="group flex items-start gap-3 rounded-lg border border-slate-200 px-4 py-3.5" data-id="element-3051" data-mp="2mo08ywv1mcc1">
            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3052" data-mp="2wl4v4wow27su">
              <FileTextIcon className="w-4 h-4" data-id="element-3053" data-mp="6wvrmbi7iw43" />
            </span>

            <div className="min-w-0 flex-1" data-id="element-3054" data-mp="bv71gm8i56mb">
              <p className="text-sm font-medium text-slate-800" data-id="element-3055" data-mp="btw00t2j4ayd">
                {tpl.name[lang]}
              </p>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3056" data-mp="1wcok3vlmt3eb">
                {tpl.scope[lang]} · {tpl.clauses}{' '}
                {t('clauses', 'clauses')}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap" data-id="element-3057" data-mp="1mmd8dmvphpwv">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3058" data-mp="1holdcjzrlnog">
                  {[{
                id: 'loisirs' as const,
                label: t('Loisirs', 'Recreation'),
                icon: PenLineIcon
              }, {
                id: 'greffe' as const,
                label: t('Greffe', 'Clerk'),
                icon: ScaleIcon
              }].map(option => {
                const Icon = option.icon;
                return <button key={option.id} onClick={() => setTemplates(prev => prev.map(item => item.id === tpl.id ? {
                  ...item,
                  author: option.id
                } : item))} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${tpl.author === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3059" data-mp="yru1gvmjyauf">
                        <Icon className="w-3 h-3" data-id="element-3060" data-mp="1o4dj7cq1o0l7" />
                        {option.label}
                      </button>;
              })}
                </div>
                <span className="text-xs text-slate-400" data-id="element-3061" data-mp="1eamv48xtxs61">
                  {t('Rédacteur', 'Drafted by')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0" data-id="element-3062" data-mp="38la0yc6lvibc">
              <span className="text-xs text-slate-500 text-right max-w-[110px]" data-id="element-3063" data-mp="wyydwb7qql9t">
                {t('Signature électronique requise', 'E-signature required')}
              </span>
              <button type="button" role="switch" aria-checked={tpl.eSignature} aria-label={t('Signature électronique requise', 'E-signature required')} onClick={() => setTemplates(prev => prev.map(item => item.id === tpl.id ? {
            ...item,
            eSignature: !item.eSignature
          } : item))} className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${tpl.eSignature ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3064" data-mp="svte4gf1oxov">
                <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${tpl.eSignature ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3065" data-mp="2xu6r492gsvjm" />
              </button>
            </div>
          </li>)}
      </ul>
    </section>;
}

// --- PAYMENTS ---------------------------------------------------------------

type TxKind = 'inscription' | 'camp' | 'location';
export function PaymentsPanel() {
  const {
    t
  } = useLanguage();
  const [methods, setMethods] = useState<Record<TxKind, string[]>>({
    inscription: ['credit', 'interac', 'comptoir'],
    camp: ['credit', 'interac', 'comptoir'],
    location: ['interac', 'comptoir']
  });
  const [holdHours, setHoldHours] = useState('48');
  const [reconciliation, setReconciliation] = useState<'auto' | 'manuelle'>('auto');
  const transactions: {
    id: TxKind;
    label: string;
    hint: string;
  }[] = [{
    id: 'inscription',
    label: t('Inscription à une activité', 'Activity registration'),
    hint: t('paiement au moment de l’inscription', 'paid at registration')
  }, {
    id: 'camp',
    label: t('Camp de jour', 'Day camp'),
    hint: t('paiement par versements possible', 'installments allowed')
  }, {
    id: 'location',
    label: t('Location de plateau', 'Facility rental'),
    hint: t('facturation après signature', 'billed after signature')
  }];
  const available = [{
    id: 'credit',
    label: t('Carte de crédit', 'Credit card'),
    icon: CreditCardIcon
  }, {
    id: 'interac',
    label: t('Virement Interac', 'Interac transfer'),
    icon: SmartphoneIcon
  }, {
    id: 'comptoir',
    label: t('Comptoir', 'Front counter'),
    icon: StoreIcon
  }];
  const toggle = (tx: TxKind, method: string) => setMethods(prev => ({
    ...prev,
    [tx]: prev[tx].includes(method) ? prev[tx].filter(m => m !== method) : [...prev[tx], method]
  }));
  const field = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]';
  return <div className="space-y-4" data-id="element-3066" data-mp="3ibtdmimeusnw">
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3067" data-mp="3ecavfulz4vor">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3068" data-mp="2qmqc3alwrwey">
          {t('Modes de paiement acceptés', 'Accepted payment methods')}
        </h2>
        <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl" data-id="element-3069" data-mp="2lpgf99w6wih3">
          {t('Chaque type de transaction peut accepter des modes différents. Un mode désactivé disparaît du parcours citoyen.', 'Each transaction type can accept different methods. A disabled method disappears from the citizen journey.')}
        </p>

        <ul className="space-y-2" data-id="element-3070" data-mp="22xxdt7htdch6">
          {transactions.map(tx => <li key={tx.id} className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3.5 flex-wrap" data-id="element-3071" data-mp="1stgxye4c3vdd">
              <div className="min-w-0" data-id="element-3072" data-mp="1xma1wzrzazoe">
                <p className="text-sm font-medium text-slate-800" data-id="element-3073" data-mp="1ivt1njr2yft3">{tx.label}</p>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3074" data-mp="1nudsiskfzn4v">{tx.hint}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-id="element-3075" data-mp="1ns1eurlpvyxm">
                {available.map(method => {
              const Icon = method.icon;
              const on = methods[tx.id].includes(method.id);
              return <button key={method.id} onClick={() => toggle(tx.id, method.id)} aria-pressed={on} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${on ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'}`} data-id="element-3076" data-mp="21kr20zczf9wb">
                      <Icon className="w-3.5 h-3.5" data-id="element-3077" data-mp="16ka6jul7hjdw" />
                      {method.label}
                      {on && <CheckIcon className="w-3 h-3" data-id="element-3078" data-mp="11kp7lk61db16" />}
                    </button>;
            })}
              </div>
            </li>)}
        </ul>
      </section>

      {/* Interac — highlighted request from the City */}
      <section className="bg-white rounded-xl border-2 border-[#4F46E5]/30 p-6" data-id="element-3079" data-mp="1pdxkh3bjqwpp">
        <div className="flex items-start gap-3 mb-5" data-id="element-3080" data-mp="1kcqacxlxllfv">
          <span className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3081" data-mp="1kfciozgnc4z5">
            <SmartphoneIcon className="w-4 h-4" data-id="element-3082" data-mp="8y4eae602ouz" />
          </span>
          <div data-id="element-3083" data-mp="8znrtx09ult2">
            <h2 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-3084" data-mp="8w0278xy6jjm">
              {t('Virement Interac', 'Interac transfer')}
              <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-semibold border border-indigo-100" data-id="element-3085" data-mp="2fnxwlf9jbnk6">
                {t('activé', 'enabled')}
              </span>
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3086" data-mp="2pj83ohesil4t">
              {t('Le citoyen reçoit une demande de fonds et règle depuis son application bancaire, sans carte de crédit.', 'The citizen receives a request for money and pays from their banking app, with no credit card.')}
            </p>
          </div>
        </div>

        {/* Citizen journey */}
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-id="element-3087" data-mp="3897kyi4e2vxo">
          {[{
          icon: SmartphoneIcon,
          title: t('1. Demande de fonds', '1. Request for money'),
          text: t('Une demande Interac est envoyée au courriel du citoyen dès la réservation.', 'An Interac request is emailed to the citizen as soon as the booking is made.')
        }, {
          icon: ArrowRightIcon,
          title: t('2. Application bancaire', '2. Banking app'),
          text: t('Le citoyen est redirigé vers son institution et approuve le virement.', 'The citizen is redirected to their institution and approves the transfer.')
        }, {
          icon: CheckIcon,
          title: t('3. Place confirmée', '3. Spot confirmed'),
          text: t('Le dépôt reçu confirme la place et le reçu est transmis automatiquement.', 'The received deposit confirms the spot and the receipt is sent automatically.')
        }].map(step => {
          const Icon = step.icon;
          return <li key={step.title} className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-4" data-id="element-3088" data-mp="28k7w99sq6ixs">
                <span className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-[#4F46E5] flex items-center justify-center mb-2.5" data-id="element-3089" data-mp="2s345lfihurre">
                  <Icon className="w-4 h-4" data-id="element-3090" data-mp="2i7u0uk0po69x" />
                </span>
                <p className="text-sm font-semibold text-slate-800" data-id="element-3091" data-mp="2n659irf9saqk">
                  {step.title}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-3092" data-mp="28gpm9ley6nfm">{step.text}</p>
              </li>;
        })}
        </ol>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3093" data-mp="2d8r6pum9rkgq">
          <div data-id="element-3094" data-mp="1zgt0brjy02o9">
            <label htmlFor="hold-hours" className="block text-xs font-medium text-slate-600 mb-1" data-id="element-3095" data-mp="1zhccermwvwud">
              <span className="inline-flex items-center gap-1.5" data-id="element-3096" data-mp="24fw0oyoz9kpw">
                <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3097" data-mp="1pjens3153y18" />
                {t('Délai avant libération automatique de la place', 'Delay before the spot is released automatically')}
              </span>
            </label>
            <div className="flex items-center gap-2" data-id="element-3098" data-mp="25xmhphyfrnwx">
              <input id="hold-hours" type="number" min="1" value={holdHours} onChange={e => setHoldHours(e.target.value)} className={`${field} max-w-[100px]`} data-id="element-3099" data-mp="25vrsvsk1nbnk" />
              <span className="text-sm text-slate-500" data-id="element-3100" data-mp="1r26iyzmwv3u7">
                {t('heures', 'hours')}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1" data-id="element-3101" data-mp="1m829a8m9ghpf">
              {t('Passé ce délai sans virement reçu, la place retourne au catalogue.', 'After this delay without a received transfer, the spot returns to the catalogue.')}
            </p>
          </div>

          <div data-id="element-3102" data-mp="13b12ci95c7iu">
            <span className="block text-xs font-medium text-slate-600 mb-1" data-id="element-3103" data-mp="13aizh2rzav1y">
              <span className="inline-flex items-center gap-1.5" data-id="element-3104" data-mp="t748phsco0yv">
                <RefreshCwIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3105" data-mp="t6ksq6m9fmnh" />
                {t('Mode de réconciliation', 'Reconciliation mode')}
              </span>
            </span>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3106" data-mp="136bi5tvyrbgp">
              {[{
              id: 'auto' as const,
              label: t('Automatique', 'Automatic')
            }, {
              id: 'manuelle' as const,
              label: t('Manuelle', 'Manual')
            }].map(option => <button key={option.id} onClick={() => setReconciliation(option.id)} className={`px-3.5 py-2 text-sm font-medium transition-colors ${reconciliation === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3107" data-mp="fdcwu2r5c4zz">
                  {option.label}
                </button>)}
            </div>
            <p className="text-xs text-slate-400 mt-1" data-id="element-3108" data-mp="wz3lfr9azbhb">
              {reconciliation === 'auto' ? t('Le dépôt confirmé par l’institution marque la transaction payée.', 'A deposit confirmed by the institution marks the transaction as paid.') : t('Un employé valide chaque virement depuis la file « Virements Interac à réconcilier ».', 'A staff member validates each transfer from the “Interac transfers to reconcile” queue.')}
            </p>
          </div>
        </div>

        <p className="flex items-start gap-2 mt-5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-[#3730A3]" data-id="element-3109" data-mp="1trpboe6265vu">
          <SparklesIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3110" data-mp="3rijiwsjgcdhs" />
          {t('Tant que le virement n’est pas reçu, la transaction reste au statut « Virement attendu » — visible partout dans le module.', 'Until the transfer is received, the transaction stays in the “Transfer pending” state — visible throughout the module.')}
        </p>
      </section>
    </div>;
}